import { Plus, SignOut } from '@phosphor-icons/react';
import { motion } from 'motion/react';
import { Link, NavLink } from 'react-router';
import { Wordmark } from '../../../shared/components/wordmark';
import { CountBadge } from '../../../shared/components/count-badge';
import { InitialAvatar } from '../../../shared/components/initial-avatar';
import { CREATE_TRANSACTION_HREF, ROUTES } from '../../../shared/constants/app.constant';
import { useLogout, useSession } from '../../auth/hooks/use-session';
import { LedgerSwitcher } from '../../ledgers/components/ledger-switcher';
import { ThemeToggle } from '../../theme/components/theme-toggle';
import { NAV_ITEMS } from '../constants/nav.constant';
import { useCanRecord } from '../hooks/use-can-record';
import { usePendingJoinRequests } from '../hooks/use-pending-join-requests';

export function SidebarNav() {
  const { user } = useSession();
  const { logout, isPending } = useLogout();
  const canRecord = useCanRecord();
  const pending = usePendingJoinRequests();

  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col gap-6 border-r border-rule px-4 py-6 lg:flex">
      <div className="flex items-center justify-between px-2">
        <Wordmark />
        <ThemeToggle />
      </div>

      <LedgerSwitcher />

      {canRecord ? (
        <Link
          to={CREATE_TRANSACTION_HREF}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-field bg-brass px-4 text-sm font-medium text-brass-ink hover:brightness-95"
        >
          <Plus size={18} weight="bold" aria-hidden="true" />
          Catat transaksi
        </Link>
      ) : null}

      <nav aria-label="Navigasi utama" className="flex flex-col gap-1">
        {NAV_ITEMS.map(({ to, label, icon: Icon, end, showPendingBadge }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `relative flex min-h-11 items-center gap-3 rounded-field px-3 text-sm ${isActive ? 'font-medium text-ink' : 'text-muted hover:text-ink'}`
            }
          >
            {({ isActive }) => (
              <>
                {isActive ? (
                  <motion.span
                    layoutId="sidebar-active"
                    className="absolute inset-0 rounded-field bg-ink/[0.06]"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                ) : null}
                <Icon size={20} weight="duotone" className="relative" aria-hidden="true" />
                <span className="relative">{label}</span>
                {showPendingBadge && pending > 0 ? (
                  <CountBadge
                    count={pending}
                    label={`${pending} permintaan bergabung`}
                    className="relative ml-auto"
                  />
                ) : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex items-center gap-1 border-t border-rule px-1 pt-4">
        <Link
          to={ROUTES.profile}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-field px-1 hover:bg-ink/5"
          aria-label={`Profil ${user?.displayName ?? ''}`}
        >
          <InitialAvatar name={user?.displayName ?? ''} />
          <span className="min-w-0 flex-1 truncate text-sm text-ink">{user?.displayName}</span>
        </Link>
        <button
          type="button"
          onClick={logout}
          disabled={isPending}
          aria-label="Keluar"
          title="Keluar"
          className="inline-flex size-11 items-center justify-center rounded-field text-muted hover:bg-ink/5 hover:text-ink"
        >
          <SignOut size={20} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
